import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useHttpHook from "../../../Hooks/HttpHook";
import { GetMyPromotingEventsApi } from "../../../utils/ApiConstant";
import { createObjectForPaginatedResult } from "../../../utils/Helper";
import MainComponentWrapper from "../../Common/MainComponentWrapper";
import MainPagination from "../../Common/MainPagination";
import PromotingEventCard from "./PromotingEventCard";
import PromotingShareModal from "./PromotingShareModal";
import { showErrorToast } from "../../../utils/notify";
import { buildPublicEventShareUrl } from "../../../utils/eventShareUrl";

const PromotingEventsList = () => {
  const [loading, sendRequest, error] = useHttpHook();
  const [eventsList, setEventsList] = useState(null);
  const [loadedTab, setLoadedTab] = useState("");
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [shareEvent, setShareEvent] = useState(null);
  const { userDetails } = useSelector((state) => state.user);
  const listRequest = useRef(0);

  useEffect(() => {
    if (error && !toast.isActive(error)) {
      showErrorToast(error);
      if (error === "You are unAuthrised") {
        window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
      }
    }
  }, [error]);

  const getEvents = async (page = 1) => {
    const requestId = ++listRequest.current;
    const tab = activeTab;
    const params = new URLSearchParams({
      page: String(page),
      tab,
    });
    const response = await sendRequest(
      `${GetMyPromotingEventsApi}?${params.toString()}`,
      { method: "get" }
    );

    if (requestId !== listRequest.current || response?.status !== 1) return;

    setLoadedTab(tab);
    if (page === 1) {
      setEventsList(response);
      return;
    }

    setEventsList((prevState) =>
      createObjectForPaginatedResult(response, prevState)
    );
  };

  useEffect(() => {
    getEvents(1);
  }, [activeTab]);

  const filteredEvents = useMemo(() => {
    const items = eventsList?.data?.itemsList || [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((event) => event.title?.toLowerCase().includes(query));
  }, [eventsList, searchQuery]);

  const buildShareUrl = (event) =>
    buildPublicEventShareUrl(event._id, { referralId: userDetails?._id });

  const handleShare = (event) => {
    setShareEvent(event);
  };

  const handleCopyPromo = async (event) => {
    const promoCode = event.promoCode || event.promocode;
    if (promoCode) {
      try {
        await navigator.clipboard.writeText(promoCode);
        toast.success("Promo code copied");
        return;
      } catch {
        toast.error("Unable to copy promo code");
        return;
      }
    }

    const shareUrl = buildShareUrl(event);
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Promo link copied");
    } catch {
      toast.error("Unable to copy promo link");
    }
  };

  return (
    <MainComponentWrapper Loading={loading} errorInfo={error}>
      <div className="event-promoting-body">
        <form
          className="event-promoting-search"
          onSubmit={(event) => event.preventDefault()}
        >
          <label htmlFor="eventPromotingSearch">Search</label>
          <input
            id="eventPromotingSearch"
            className="event-promoting-search-input"
            type="search"
            name="eventName"
            placeholder="search by event name..."
            autoComplete="off"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </form>

        <nav className="event-promoting-tabs" aria-label="Promoting event categories">
          {[
            { id: "upcoming", label: "Upcoming" },
            { id: "expired", label: "Expired" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`event-promoting-tab${activeTab === tab.id ? " is-active" : ""
                }`}
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {loadedTab === activeTab && filteredEvents.length > 0 ? (
          <MainPagination getEventsMethod={getEvents} MainData={eventsList}>
            <div
              className="event-promoting-grid"
              role="list"
              aria-label="Promoting events list"
            >
              {filteredEvents.map((event) => (
                <PromotingEventCard
                  key={event._id}
                  event={event}
                  onShare={handleShare}
                  onCopyPromo={handleCopyPromo}
                />
              ))}
            </div>
          </MainPagination>
        ) : loadedTab === activeTab ? (
          <div className="event-promoting-empty">
            <i className="bi bi-megaphone" aria-hidden="true"></i>
            <p>
              No {activeTab} promoting events found
              {searchQuery.trim() ? " for your search." : "."}
            </p>
          </div>
        ) : null}
      </div>

      <PromotingShareModal
        show={Boolean(shareEvent)}
        onHide={() => setShareEvent(null)}
        shareUrl={shareEvent ? buildShareUrl(shareEvent) : ""}
        promoCode={shareEvent?.promoCode || shareEvent?.promocode || ""}
      />
    </MainComponentWrapper>
  );
};

export default PromotingEventsList;
